/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  PaginationInfo,
  PaginationInfoApiResponse,
  paginationInfoFromApiResponse,
} from '@shared/models/pagination-info';
import {Role, RoleApiResponse, roleFromApiResponse} from '@shared/models/roles';
import {isEmpty} from 'lodash-es';

export interface TeamUser {
  userId: string;
  resourceName: string;
  firstName: string;
  lastName: string;
  email: string;
  accountId: string;
  mobilePhoneNumber: string;
  createdAt: string;
  updatedAt: string;
  roles: Role[];
  loginId?: string;
}

/**
 * This interface describes how the TeamUserApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface TeamUserApiResponse {
  user_id: string;
  resource_name: string;
  first_name: string;
  last_name: string;
  email: string;
  account_id: string;
  mobile_phone_number: string;
  created_at: string;
  updated_at: string;
  roles: RoleApiResponse[];
  login_id?: string;
}

/**
 * This method transforms the TeamUserApiResponse into our own
 * TeamUser object which our application understands
 * @param response the pagination API response
 * @returns the deserialized TeamUser object
 */
export const teamUserFromApiResponse = (response: TeamUserApiResponse): TeamUser => {
  return {
    userId: response.user_id,
    resourceName: response.resource_name,
    firstName: response.first_name,
    lastName: response.last_name,
    email: response.email,
    accountId: response.account_id,
    mobilePhoneNumber: response.mobile_phone_number,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    roles: response.roles.map((roleResponse: RoleApiResponse) =>
      roleFromApiResponse(roleResponse)
    ),
    ...(response.login_id && {loginId: response.login_id}),
  };
};

export interface TeamPagination {
  team: TeamUser[];
  pagination: PaginationInfo;
}

/**
 * This interface describes how the TeamPaginationApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface TeamPaginationApiResponse {
  users: Array<TeamUserApiResponse>;
  pagination_info: PaginationInfoApiResponse;
}

/**
 * This method transforms the TeamPaginationApiResponse into our own
 * TeamPagination object which our application understands
 * @param response the pagination API response
 * @returns the deserialized TeamPagination
 */
export const teamPaginationFromResponse = (
  response: TeamPaginationApiResponse
): TeamPagination => {
  // TODO: Remove code when API GW returns empty array instead of empty object when there are no items
  response.users = !isEmpty(response?.users) ? response.users : [];

  return {
    team: response.users.map((teamResponse: TeamUserApiResponse) =>
      teamUserFromApiResponse(teamResponse)
    ),
    pagination: paginationInfoFromApiResponse(response.pagination_info),
  };
};

export interface InvitedUser {
  accountId: string;
  createdAt: string;
  userInvitationId: string;
  email: string;
  firstName: string;
  lastName: string;
  updatedAt: string;
  roles: string[];
}
export interface InvitedUserApiResponse {
  account_id: string;
  created_at: string;
  user_invitation_id: string;
  email: string;
  first_name: string;
  last_name: string;
  updated_at: string;
  roles: string[];
}

export const invitedUserFromApiResponse = (response: InvitedUserApiResponse): InvitedUser => {
  return {
    accountId: response.account_id,
    createdAt: response.created_at,
    userInvitationId: response.user_invitation_id,
    email: response.email,
    firstName: response.first_name,
    lastName: response.last_name,
    updatedAt: response.updated_at,
    roles: response.roles,
  };
};

export interface UserInvitation {
  userInvitationId: string;
  email: string;
  accountId: string;
  roleIds: string[];
  status: UserInvitationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface UserInvitationApiResponse {
  user_invitation_id: string;
  email: string;
  account_id: string;
  status: UserInvitationStatus;
  role_ids: string[];
  created_at: string;
  updated_at: string;
}

export const userInvitationFromApiResponse = (
  response: UserInvitationApiResponse
): UserInvitation => {
  return {
    userInvitationId: response.user_invitation_id,
    email: response.email,
    accountId: response.account_id,
    roleIds: response.role_ids,
    status: response.status,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
};

export interface UserInvitationPagination {
  userInvitations: UserInvitation[];
  pagination: PaginationInfo;
}

export interface UserInvitationPaginationApiResponse {
  user_invitations: UserInvitationApiResponse[];
  pagination_info: PaginationInfoApiResponse;
}

export const userInvitationPaginationFromResponse = (
  response: UserInvitationPaginationApiResponse
): UserInvitationPagination => {
  return {
    userInvitations: response.user_invitations.map(
      (userInvitationResponse: UserInvitationApiResponse) =>
        userInvitationFromApiResponse(userInvitationResponse)
    ),
    pagination: paginationInfoFromApiResponse(response.pagination_info),
  };
};

export enum UserInvitationStatus {
  UserInvitationStatusUnspecified = 'USER_INVITATION_STATUS_UNSPECIFIED',
  UserInvitationStatusPending = 'USER_INVITATION_STATUS_PENDING',
  UserInvitationStatusAccepted = 'USER_INVITATION_STATUS_ACCEPTED',
  UserInvitationStatusExpired = 'USER_INVITATION_STATUS_EXPIRED',
}
